import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  Plus, Palette, MapPin, Tag, Clock, CheckCircle2, Archive,
  MessageSquare, SlidersHorizontal, X, Check,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  freelanceGigsApi,
  FreelanceGigWithUnread,
  FreelanceGigStatus,
} from '../services/freelanceGigsApi';
import api from '../services/api';
import { useGigSectorPrefs } from '../hooks/useGigSectorPrefs';
import './FreelanceGigs.scss';

/**
 * Employer's "Mano Freelance Gigs" list.
 * Status tabs + sector preference bar (shared with employee browse, saved in DB).
 */
const FreelanceGigsList: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [gigs, setGigs] = useState<FreelanceGigWithUnread[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FreelanceGigStatus | 'all'>('all');

  // Sector preference (persisted in DB)
  const { selectedSectors, setSectors } = useGigSectorPrefs();
  const [allSectors, setAllSectors] = useState<string[]>([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [staged, setStaged] = useState<string[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { gigs } = await freelanceGigsApi.myGigs();
      setGigs(gigs);
    } catch (err) {
      console.error('Failed to load gigs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.user_type === 'employer') load();
  }, [isAuthenticated, user, load]);

  useEffect(() => {
    api.get('/cv-templates?grouped=true')
      .then(res => {
        const data = res.data?.data ?? res.data ?? {};
        setAllSectors((data.work_field ?? []).map((t: any) => t.value as string));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!panelOpen) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [panelOpen]);

  if (isLoading) return <div className="freelance-page-loading">Kraunama...</div>;
  if (!isAuthenticated) return <Navigate to="/login?type=employer&mode=employer" replace />;
  if (user?.user_type !== 'employer') return <Navigate to="/" replace />;

  // Apply both status and sector filters client-side (employer sees own gigs only)
  const statusFiltered = filter === 'all' ? gigs : gigs.filter(g => g.status === filter);
  const filtered = selectedSectors.length > 0
    ? statusFiltered.filter(g => g.work_fields.some(f => selectedSectors.includes(f)))
    : statusFiltered;

  const counts = {
    all: gigs.length,
    draft: gigs.filter(g => g.status === 'draft').length,
    published: gigs.filter(g => g.status === 'published').length,
    closed: gigs.filter(g => g.status === 'closed').length,
  };

  const openPanel = () => { setStaged([...selectedSectors]); setPanelOpen(true); };
  const applyPanel = () => { setSectors(staged); setPanelOpen(false); };
  const removeSector = (s: string) => setSectors(selectedSectors.filter(x => x !== s));
  const clearAll = () => setSectors([]);

  return (
    <div className="freelance-gigs-page">
      <div className="freelance-page-header">
        <div className="freelance-page-title">
          <Palette size={22} />
          <h1>Freelance Gigs</h1>
        </div>
        <Link to="/freelance/gigs/new?mode=employer" className="btn btn-primary">
          <Plus size={16} /> Naujas Gig
        </Link>
      </div>

      {/* Sector preference bar */}
      <div className="gig-sector-bar">
        <div className="gig-sector-bar__chips">
          {selectedSectors.length === 0 ? (
            <span className="gig-sector-chip gig-sector-chip--all">Visos sritys</span>
          ) : (
            selectedSectors.map(s => (
              <span key={s} className="gig-sector-chip gig-sector-chip--active">
                {s}
                <button type="button" className="gig-sector-chip__remove" onClick={() => removeSector(s)}>
                  <X size={12} />
                </button>
              </span>
            ))
          )}
        </div>
        <div className="gig-sector-bar__actions">
          {selectedSectors.length > 0 && (
            <button type="button" className="gig-sector-clear" onClick={clearAll}>
              <X size={14} /> Išvalyti
            </button>
          )}
          <div className="gig-sector-picker-wrap" ref={panelRef}>
            <button type="button" className="gig-sector-pick-btn" onClick={openPanel}>
              <SlidersHorizontal size={15} /> Pasirinkti sritis
            </button>
            {panelOpen && (
              <div className="gig-sector-panel">
                <div className="gig-sector-panel__header">
                  <span>Mano sritys</span>
                  <button type="button" onClick={() => setPanelOpen(false)}><X size={16} /></button>
                </div>
                <div className="gig-sector-panel__list">
                  {allSectors.map(s => {
                    const checked = staged.includes(s);
                    return (
                      <label key={s} className={`gig-sector-option ${checked ? 'checked' : ''}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setStaged(prev => checked ? prev.filter(x => x !== s) : [...prev, s])
                          }
                        />
                        <span className="gig-sector-option__box">
                          {checked && <Check size={11} strokeWidth={3} />}
                        </span>
                        {s}
                      </label>
                    );
                  })}
                </div>
                <div className="gig-sector-panel__footer">
                  <button type="button" className="btn-secondary btn-sm" onClick={() => setStaged([])}>Išvalyti</button>
                  <button type="button" className="btn-primary btn-sm" onClick={applyPanel}>
                    Taikyti ({staged.length > 0 ? staged.length : 'visos'})
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {gigs.length === 0 && !loading ? (
        <FreelanceEmptyState />
      ) : (
        <>
          <div className="freelance-status-tabs">
            <FilterTab label="Visi" count={counts.all} active={filter === 'all'} onClick={() => setFilter('all')} />
            <FilterTab label="Juodraščiai" count={counts.draft} active={filter === 'draft'} onClick={() => setFilter('draft')} />
            <FilterTab label="Paskelbti" count={counts.published} active={filter === 'published'} onClick={() => setFilter('published')} />
            <FilterTab label="Uždaryti" count={counts.closed} active={filter === 'closed'} onClick={() => setFilter('closed')} />
          </div>

          {loading ? (
            <div className="freelance-page-loading">Kraunama...</div>
          ) : filtered.length === 0 ? (
            <p className="freelance-empty-hint">
              {selectedSectors.length > 0
                ? 'Pasirinktose srityse Gigų nėra.'
                : 'Šiuo statusu Gigų nėra.'}
            </p>
          ) : (
            <div className="freelance-gigs-grid">
              {filtered.map(g => <GigCard key={g.id} gig={g} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const FilterTab: React.FC<{ label: string; count: number; active: boolean; onClick: () => void }> = ({ label, count, active, onClick }) => (
  <button type="button" className={`freelance-filter-tab ${active ? 'is-active' : ''}`} onClick={onClick}>
    {label} <span className="freelance-filter-count">{count}</span>
  </button>
);

const GigCard: React.FC<{ gig: FreelanceGigWithUnread }> = ({ gig }) => {
  const StatusIcon = gig.status === 'published' ? CheckCircle2 : gig.status === 'closed' ? Archive : Clock;
  return (
    <Link to={`/freelance/gigs/${gig.id}?mode=employer`} className="freelance-gig-card">
      <div className="freelance-gig-card-top">
        <div className={`freelance-gig-status freelance-gig-status--${gig.status}`}>
          <StatusIcon size={14} />
          {gig.status === 'published' ? 'Paskelbta' : gig.status === 'closed' ? 'Uždaryta' : 'Juodraštis'}
        </div>
        {gig.unread_count > 0 && (
          <span className="freelance-unread-pill" title={`${gig.unread_count} neperskaitytos žinutės`}>
            <MessageSquare size={11} />{gig.unread_count}
          </span>
        )}
      </div>
      <h3 className="freelance-gig-title">{gig.title}</h3>
      <p className="freelance-gig-desc">{gig.description}</p>
      <div className="freelance-gig-meta">
        {gig.work_fields.length > 0 && (
          <span className="freelance-gig-tag"><Tag size={12} /> {gig.work_fields.join(', ')}</span>
        )}
        <span className="freelance-gig-tag"><MapPin size={12} />
          {gig.nationwide ? 'Visoje Lietuvoje' : (gig.city || 'Nenurodyta')}
        </span>
        {gig.budget_text && <span className="freelance-gig-tag">{gig.budget_text}</span>}
      </div>
    </Link>
  );
};

const FreelanceEmptyState: React.FC = () => (
  <div className="freelance-empty-state">
    <div className="freelance-empty-icon"><Palette size={42} /></div>
    <h2>Dar neturite nei vieno Freelance Gig</h2>
    <p>Paskelbkite trumpą užduotį ir mes el. paštu iškart išsiųsime ją laisvai
    samdomiems specialistams, kurių sritys atitinka.</p>
    <Link to="/freelance/gigs/new?mode=employer" className="btn btn-primary btn-lg">
      <Plus size={16} /> Sukurk pirmą Freelance Gig!
    </Link>
  </div>
);

export default FreelanceGigsList;
